import type { FeedbackItem } from './api.ts'
import type { Route } from './route.ts'
import { REASON_LABEL } from '../components/DecisionActions.tsx'

/**
 * What the pharmacist said, as the follow-up queue shows it (#405).
 *
 * A decision is stored as a verdict, a flag or two, a reason code and a note,
 * and none of them alone says what happened: "corrected" means "we agree, will
 * fix" on a finding we flagged and "you missed this" on one we did not. This
 * turns the row into the sentence somebody judging it needs.
 */
export interface FeedbackAnswer {
  /** The answer itself: "Não é divergência", "Comentário". */
  readonly answer: string
  /** Whether the answer disagrees with us, so the screen can colour it. */
  readonly contests: boolean
  /** The reason they picked, in the words they picked it from. */
  readonly reason: string | undefined
  /** Their own words, when they wrote any. */
  readonly note: string | undefined
}

export function describeFeedback(item: FeedbackItem): FeedbackAnswer {
  if (item.kind === 'feedback') {
    return { answer: 'Comentário', contests: false, reason: undefined, note: text(item.notes) }
  }

  const code = item.detail?.reasonCode
  const reason =
    code !== undefined
      ? (REASON_LABEL[code] ?? code)
      : // Before #405 the code was not carried into the trail; the misreading
        // flag was, and it is the one reason worth recovering.
        item.misread
        ? REASON_LABEL['ai-misread']
        : undefined

  return {
    answer: item.missed
      ? 'Apontou uma divergência que não sinalizamos'
      : (ANSWER[item.decision ?? ''] ?? item.decision ?? '—'),
    contests: item.missed || item.decision === 'accepted' || item.decision === 'rejected',
    reason,
    // The histórico keeps a decision's note in `reason`; see recordConclusion.
    note: text(item.reason),
  }
}

const ANSWER: Readonly<Record<string, string>> = {
  accepted: 'Não é divergência',
  rejected: 'Não procede',
  corrected: 'Corrigir no sistema',
}

function text(value: string | null): string | undefined {
  const trimmed = value?.trim() ?? ''
  return trimmed === '' ? undefined : trimmed
}

/**
 * The conferência a feedback is about, opened on the apontamento itself
 * rather than on the conferência's first step.
 */
export function feedbackRoute(item: FeedbackItem): Route {
  const preparation = item.detail?.evidence?.['preparationId']
  return {
    name: 'verification',
    at: { kind: 'identified', id: item.verification_id },
    finding: item.fingerprint ?? undefined,
    near: {
      ...(typeof preparation === 'string' ? { preparation } : {}),
      ...(item.detail?.check === undefined ? {} : { check: item.detail.check }),
    },
  }
}
